import React, { FunctionComponent } from "react";
import styles from "./DarkModeSwitch.module.css";
import { connect, useDispatch } from "react-redux";
import { updateTheme } from "../../Redux/actions";

// ---- Types ----

type DarkModeSwitchProps = {
  /** Current theme value from Redux: true = light mode, false = dark mode */
  theme: boolean;
  /** Any additional props injected by connect (kept as any to avoid breaking changes) */
  [key: string]: any;
};

/**
 * DarkModeSwitch
 *
 * A toggle switch that:
 * - Reads the current theme (light/dark) from Redux via `theme` prop.
 * - Dispatches `updateTheme` when the checkbox is toggled.
 * - Persists theme preference in `localStorage` under key `isLightMode`.
 *
 * Visuals are controlled via CSS module `DarkModeSwitch.module.css`
 * and Font Awesome icons for sun (light) and moon (dark).
 */
const DarkModeSwitch: FunctionComponent<DarkModeSwitchProps> = ({ theme }) => {
  const dispatch = useDispatch();

  /**
   * Handles checkbox toggle.
   *
   * When checked:
   * - Dispatches `updateTheme(true)` → sets light mode.
   * - Saves `"true"` to `localStorage.isLightMode`.
   *
   * When unchecked:
   * - Dispatches `updateTheme(false)` → sets dark mode.
   * - Saves `"false"` to `localStorage.isLightMode`.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    dispatch(updateTheme(isChecked));
    localStorage.setItem("isLightMode", isChecked ? "true" : "false");
  };

  return (
    <div>
      {/* 
        Controlled checkbox:
        - `checked` is driven by Redux `theme`
        - `onChange` updates Redux + localStorage
      */}
      <input
        type="checkbox"
        checked={theme}
        onChange={handleChange}
        className={styles.checkbox}
        id="checkbox"
      />

      {/* Label styled as a switch with icons for moon and sun */}
      <label htmlFor="checkbox" className={styles.label}>
        {/* Moon icon for dark mode */}
        <i className="fas fa-moon" style={{ color: "white" }} />

        {/* Sun icon for light mode */}
        <i className="fas fa-sun" style={{ color: "yellow" }} />

        {/* Sliding ball of the toggle switch */}
        <div className={styles.ball}></div>
      </label>
    </div>
  );
};

// ---- Redux mapping ----

/**
 * Maps Redux state to component props.
 *
 * Note: `snackbarData` is still mapped to avoid any potential
 * external dependency on this prop, even though it is not used
 * directly inside `DarkModeSwitch`.
 */
const mapStateToProps = (state: any) => {
  return {
    snackbarData: state.snackbar,
    theme: state.theme.isLightMode,
  };
};

export default connect(mapStateToProps)(DarkModeSwitch);
